import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Box, Button, Paper, Slider } from '@mui/material';
import ColorWheel from '../components/ColorWheel';
import { getRgbaColor, getTextColor } from '../utils/colorUtils';

const Draw = ({ settings }) => {
  const [selectedColor, setSelectedColor] = useState('rgb(255, 0, 0)');
  const [brightness, setBrightness] = useState(100);
  const [grid, setGrid] = useState([]);
  const [action, setAction] = useState('draw');
  const { width, height } = settings;

  useEffect(() => {
    setGrid(
      Array.from({ length: height }, (_, rowIndex) =>
        Array.from({ length: width }, (_, colIndex) => ({
          color: 'rgba(255, 255, 255, 1)',
          index: rowIndex * width + colIndex,
        }))
      )
    );
  }, [width, height]);

  const handleCellClick = (row, col) => {
    const rgbaColor = getRgbaColor(selectedColor, brightness);

    const updatedGrid = grid.map((gridRow, rowIndex) =>
      gridRow.map((cell, colIndex) => {
        if (action === 'erase') {
          return rowIndex === row && colIndex === col
            ? { ...cell, color: 'rgba(255,255,255,1)' }
            : cell;
        } else if (action === 'row' && rowIndex === row) {
          return { ...cell, color: rgbaColor };
        } else if (action === 'column' && colIndex === col) {
          return { ...cell, color: rgbaColor };
        } else if (action === 'fill') {
          return { ...cell, color: rgbaColor };
        }
        return rowIndex === row && colIndex === col
          ? { ...cell, color: rgbaColor }
          : cell;
      })
    );

    setGrid(updatedGrid);
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h5" align="center" gutterBottom>
        Draw Page
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          marginTop: 2,
        }}
      >
        <Box sx={{ flex: 1, maxWidth: '70%' }}>
          <ColorWheel color={selectedColor} onChange={setSelectedColor} />
        </Box>

        <Box sx={{ height: '70%', display: 'flex', alignItems: 'center' }}>
          <Slider
            orientation="vertical"
            value={brightness}
            onChange={(e, newValue) => setBrightness(newValue)}
            valueLabelDisplay="auto"
            min={0}
            max={100}
            sx={{
              height: '150px',
              '& .MuiSlider-thumb': { width: 16, height: 16 },
            }}
          />
        </Box>
      </Box>

      <Box sx={{ marginTop: 2, textAlign: 'center' }}>
        <Typography sx={{ marginBottom: 1, fontWeight: 'bold' }}>
          Selected Color:
        </Typography>
        <Paper
          sx={{
            width: '100%',
            height: '50px',
            backgroundColor: getRgbaColor(selectedColor, brightness),
            color: getTextColor(selectedColor, brightness),
            border: '1px solid #ddd',
          }}
        >
          <Typography>Brightness: {brightness}%</Typography>
          <Typography>
            Color: {getRgbaColor(selectedColor, brightness)}
          </Typography>
        </Paper>
      </Box>

      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}
      >
        {['draw', 'erase', 'row', 'column', 'fill'].map((mode) => (
          <Button
            key={mode}
            variant={action === mode ? 'contained' : 'outlined'}
            onClick={() => setAction(mode)}
          >
            {mode.charAt(0).toUpperCase() + mode.slice(1)}
          </Button>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${width}, 20px)`,
          gap: '2px',
          justifyContent: 'center',
          marginTop: 3,
        }}
      >
        {grid.map((row, rowIndex) =>
          row.map(({ color, index }, colIndex) => (
            <Box
              key={`${rowIndex}-${colIndex}`}
              sx={{
                width: '20px',
                height: '20px',
                backgroundColor: color,
                border: '1px solid #ddd',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: '#000',
              }}
              onClick={() => handleCellClick(rowIndex, colIndex)}
            >
              {index}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default Draw;
